// @ts-check
import classNames from 'classnames';
import { graphql } from 'gatsby';
import React from 'react';
import InputContainer from '~/components/Common/InputContainer';
import Link from '~/components/Common/Link';
import config from '~/config';
import { useContentStackProps } from '~/contentstack';
import { GetCopyFromJson } from '~/contentstack/rendering';
import { UtmParser } from '~/utils/UtmParser';

import './ReachOut.scss';

let MktoForms2;
function initMarketo() {
	// escape Gatsby build
	if (!window) {
		return;
		// @ts-ignore
	} else if (!window.MktoForms2) {
		console.warn('[DEBUG] Marketo lib missing for contact form!');
		return;
	}

	// @ts-ignore
	MktoForms2 = window.MktoForms2;
}

export const ReachOut_REFERENCES = ['reach_out'];

export const query = graphql`
	fragment ReachOutFragment on Ozk_page {
		reach_out {
			uid
			header
			body {
				phone_number_link {
					title
					href
				}
				copy {
					json
				}
			}
			form {
				first_name
				last_name
				email
				phone
				zipcode
				select_field {
					field_label
					field_key
					field_options {
						option_key
						option_label
					}
				}
				message
				submit_button_text
				bottom_right_link {
					title
					href
				}
			}
		}
	}
`;

/**
 * @typedef ReachOutSelectOption
 * @property {string} option_key
 * @property {string} option_label

 * @typedef ReachOutSelectOptions
 * @property {string} field_key
 * @property {string} field_label
 * @property {ReachOutSelectOption[]} field_options

 * @typedef ReachOutFormOptions
 * @property {import('../CSTypes').CSLink} bottom_right_link
 * @property {string} email
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} message
 * @property {string} phone
 * @property {string} zipcode
 * @property {ReachOutSelectOptions[]} select_field
 * @property {string} submit_button_text

 * @typedef ReachOutBodyOptions
 * @property {import('../CSTypes').CSLink} phone_number_link
 * @property {import('../CSTypes').CSJRTE} copy

 * @typedef ReachOutCS
 * @property {string} uid
 * @property {string} header
 * @property {ReachOutBodyOptions[]} body
 * @property {ReachOutFormOptions} form
 * 
 * @typedef ReachOutProps
 * @property {Location} location
 * @property {ReachOutCS} options
 */
/** @type {React.FC<ReachOutProps>} */
const ReachOut = ({ location, options }) => {
	// parseInt because Octopus vars are all strings
	const formId = parseInt(config.MarketoFormId, 10) ?? 1069;
	const RESET_TIME_MS = 60 * 1000;

	// Get Marketo sdk
	React.useEffect(() => {
		initMarketo();
	}, []);

	const [model] = useContentStackProps({
		staticModel: options,
		contentType: 'reach_out',
		contentUid: options?.uid,
	});

	/**
	 * A function used to bind the given form to the the current React app.
	 */
	function hookUpForm(form) {
		// If the form fails to load, due to Marketo Api (temp unavailable, 600+), we get null here.
		if (!form) return;

		form.onValidate((valid) => {
			setState({
				isValid: valid,
			});

			const reqFields = [
				document.getElementById('formFirstName'),
				document.getElementById('formEmail'),
				document.getElementById('formZipCode'),
				document.getElementById('formMsg'),
			];

			// show or hide formatting on required fields
			reqFields.forEach((el) => {
				if (el?.id == 'formZipCode') {
					// validate zip code manually
					valid = document.getElementById('zipcode').value != '';

					setState({
						isValid: valid,
					});
				}
				valid ? el?.classList.remove('ReachOut__required') : el?.classList.add('ReachOut__required');
			});

			// only submit when form is valid
			form.submittable(valid);
		});

		form.onSubmit(() => {
			setState({
				isLoading: true,
				isSuccess: false,
				isError: false,
			});
		});

		form.onSuccess((_values, _successUrl) => {
			setState({
				isSuccess: true,
				isLoading: false,
				isError: false,
			});

			// @ts-ignore
			document.getElementById('reachOutForm').reset();

			// reset form state
			setState({
				firstName: '',
				lastName: '',
				email: '',
				phone: '',
				zipcode: '',
				selectFields: {},
				message: '',
			});

			// wait a bit and then hide success message
			setTimeout(() => {
				setState({ isSuccess: false });
			}, RESET_TIME_MS);

			return false;
		});
	}

	// Reset the page if the location changes
	React.useEffect(() => {
		const foundForm = MktoForms2?.getForm(formId);
		if (foundForm) {
			hookUpForm(foundForm);
		} else {
			MktoForms2?.loadForm(config.MarketoUrl, config.MarketoToken, formId, hookUpForm);
		}
		// Re-bind form when the page changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname]);

	/**
	 * @typedef ReachOutState
	 * @property {string} firstName
	 * @property {string} lastName
	 * @property {string} email
	 * @property {string} phone
	 * @property {string} message
	 * @property {string} zipcode
	 * @property {Record<string, string>} selectFields
	 * @property {boolean} isError
	 * @property {boolean} isLoading
	 * @property {boolean} isSuccess
	 * @property {boolean} isValid
	 */
	/** @type {ReachOutState} */
	const initial = {
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		message: '',
		zipcode: '',
		selectFields: {},
		isError: false,
		isLoading: false,
		isSuccess: false,
		isValid: true,
	};
	const [state, _setState] = React.useState(initial);
	/** @param {Partial<ReachOutState>} partial */
	const setState = (partial) =>
		_setState((prev) => ({
			...prev,
			...partial,
		}));

	/**
	 * @param {string} key
	 * @param {import('react').FormEvent<HTMLElement>} event
	 */
	const onFieldChange = (key, event) =>
		setState({
			[key]: event.target['value'],
		});

	/**
	 * @param {string} key The select field key of this option.
	 * @param {import('react').FormEvent<HTMLElement>} event
	 */
	const onSelectFieldChange = (key, event) => {
		setState({
			selectFields: {
				...state.selectFields,
				[key]: event.target?.['value'],
			},
		});
	};

	const handleSubmit = (ev) => {
		ev.preventDefault();

		try {
			const thisHref = window?.location?.href ?? location?.href ?? '';
			const utmParser = new UtmParser(thisHref);
			utmParser.initUtmParamObj(window);

			const utm = {
				utmsource: utmParser.getUtmParam('source', 'direct'),
				utmmedium: utmParser.getUtmParam('medium', 'not_provided'),
				utmname: utmParser.getUtmParam('campaign') ?? utmParser.getUtmParam('name', 'not_provided'),
				utmterm: utmParser.getUtmParam('term', 'not_provided'),
				utmcontent: utmParser.getUtmParam('content', 'not_provided'),
			};

			MktoForms2.whenReady((form) => {
				// populate Marketo form values from state and utm
				const formVals = Object.assign(
					// IMMUTABLE NAMES - these *must* match the external Marketo form configuration.
					{
						Email: state.email,
						Phone: state.phone,
						FirstName: state.firstName,
						LastName: state.lastName,
						PostalCode: state.zipcode,
						commentCapture: state.message,
						referringURL: window.location.pathname,
					},
					state.selectFields,
					utm
				);
				form.vals(formVals);

				form.submit();
			});
		} catch (e) {
			console.error(e);
			setState({
				isError: true,
				isLoading: false,
				isSuccess: false,
			});
		}

		try {
			// for FB Pixel in GTM
			// @ts-ignore
			window.dataLayer.push({
				email: state.email,
				first_name: state.firstName,
				last_name: state.lastName,
			});
		} catch (e) {
			console.error(e);
		}
	};

	// If the model has not yet been fetched, just skip.
	// In non-LP, this is a static query built into the code, it will NEVER be empty.
	if (!model?.body) {
		return null;
	}

	// Max/min aren't strictly enforced - add minor guard rails for any of those fields.
	/** @param {import('react').ChangeEvent<HTMLInputElement>} ev */
	function enforceMinMax(ev) {
		if (ev.target.type !== 'number') {
			return;
		}

		const maxAttr = ev.target.getAttribute('max');
		const maxInt = parseInt(maxAttr);
		if (maxInt && parseInt(ev.target.value) > maxInt) {
			ev.target.value = maxAttr;
			return;
		}

		const minAttr = ev.target.getAttribute('min');
		const minInt = parseInt(minAttr);
		if (minInt && parseInt(ev.target.value) < minInt) {
			ev.target.value = minAttr;
			return;
		}
	}

	return (
		<div className="container">
			<div className="ReachOut" id="reach-out" role="form">
				<div className="row">
					<div className="col-md-6">
						<ReachOutBody options={model} />

						<form id="reachOutForm" className="ReachOut__form" method="post">
							<InputContainer id="formFirstName" className="ReachOut OZRK__input-container" icon="person">
								<label htmlFor="firstName" tabIndex={-1}>
									{model.form.first_name}
								</label>
								<input
									id="firstName"
									type="text"
									onChange={(ev) => onFieldChange('firstName', ev)}
									autoComplete="given-name"
									aria-label={model.form.first_name}
									maxLength={100}
								/>
								{!state.isValid && (
									<p className="ReachOut__required--error ReachOut__required--error-input">*This field is required.</p>
								)}
							</InputContainer>
							<InputContainer id="formLastName" className="ReachOut OZRK__input-container" icon="person">
								<label htmlFor="lastName" tabIndex={-1}>
									{model.form.last_name}
								</label>
								<input
									id="lastName"
									type="text"
									onChange={(ev) => onFieldChange('lastName', ev)}
									autoComplete="family-name"
									aria-label={model.form.last_name}
									maxLength={100}
								/>
							</InputContainer>
							<InputContainer id="formEmail" className="ReachOut OZRK__input-container" icon="email">
								<label htmlFor="email" tabIndex={-1}>
									{model.form.email}
								</label>
								<input
									id="email"
									type="email"
									onChange={(ev) => onFieldChange('email', ev)}
									autoComplete="email"
									aria-label={model.form.email}
									maxLength={200}
								/>
								{!state.isValid && (
									<p className="ReachOut__required--error ReachOut__required--error-input">*This field is required.</p>
								)}
							</InputContainer>
							<InputContainer id="formPhone" className="ReachOut OZRK__input-container" icon="phone">
								<label htmlFor="phone" tabIndex={-1}>
									{model.form.phone}
								</label>
								<input
									id="phone"
									type="tel"
									name="phone"
									onChange={(ev) => onFieldChange('phone', ev)}
									autoComplete="tel"
									aria-label={model.form.phone}
									maxLength={25}
								/>
							</InputContainer>

							{/* OPTIONAL Zip Code field */}
							{model.form.zipcode && (
								<InputContainer id="formZipCode" className="ReachOut OZRK__input-container" icon="home">
									<label htmlFor="zipcode" tabIndex={-1}>
										{model.form.zipcode}
									</label>
									<input
										id="zipcode"
										type="text"
										onChange={(ev) => {
											enforceMinMax(ev);
											return onFieldChange('zipcode', ev);
										}}
										autoComplete="postal-code"
										aria-label={model.form.zipcode}
										min={1}
										max={99999}
									/>
									{!state.isValid && (
										<p className="ReachOut__required--error ReachOut__required--error-input">
											*This field is required.
										</p>
									)}
								</InputContainer>
							)}

							{model.form.select_field?.map((selectFieldOpt) => (
								<InputContainer
									id="formSelectField"
									className="ReachOut OZRK__input-container"
									icon="assessment"
									key={selectFieldOpt.field_key}
								>
									<label className="ReachOut__select--label" htmlFor={selectFieldOpt.field_key} tabIndex={-1}>
										{selectFieldOpt.field_label}
									</label>
									<select
										id={selectFieldOpt.field_key}
										onChange={(ev) => onSelectFieldChange(selectFieldOpt.field_key, ev)}
										aria-label={selectFieldOpt.field_label}
									>
										{selectFieldOpt.field_options.map((opt, i) => (
											<option key={`opt-${selectFieldOpt.field_key}-${opt.option_key}`} value={opt.option_key}>
												{opt.option_label}
											</option>
										))}
									</select>
									<div style={{ paddingRight: '1rem' }}>
										<i className="material-icons">arrow_drop_down</i>
									</div>
								</InputContainer>
							))}

							<textarea
								id="formMsg"
								className="ReachOut__textarea"
								name="message"
								onChange={(ev) => onFieldChange('message', ev)}
								placeholder={model.form.message}
								aria-label={model.form.message}
								maxLength={500}
							/>
							{!state.isValid && <p className="ReachOut__required--error">*This field is required.</p>}

							{state.isSuccess && (
								<div id="successMsg" className="col-lg-12 ReachOut__success--visible">
									<p>
										Thank you for reaching out. Your message has been received and is enroute to the appropriate team
										for response.
										<br />
										<br />
										Our Customer Care Team is available to help you Monday through Friday from 7am to 7pm CT and
										Saturday from 7am to 4pm CT. Messages received outside these hours will have a longer response time.
										<br />
										<br />
										Thanks again and we will be in touch soon!
									</p>
								</div>
							)}
							{state.isError && (
								<div id="errorMsg" className="col-lg-12 ReachOut__error--visible">
									<p>
										Our apologies, there was an error submitting your feedback.
										<br />
										<br />
										Please try disabling ad blocking extensions in your browser and reload the page.
									</p>
								</div>
							)}

							<div className="col-lg-12">
								<div className="row" style={{ margin: '0 -30px' }}>
									<div className="col-md-6">
										<button
											onClick={handleSubmit}
											type="submit"
											aria-label="Send Message"
											disabled={state.isLoading}
											className={classNames('btn btn--primary btn--upper ReachOut__btn', {
												'ReachOut__btn--loading': state.isLoading,
											})}
										>
											{model.form.submit_button_text}
										</button>
									</div>
									<div className="col-md-6 ReachOut__lost">
										<br />
										<Link to={model.form.bottom_right_link.href} title={model.form.bottom_right_link.title}>
											{model.form.bottom_right_link.title}
										</Link>
									</div>
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

/**
 * @typedef ReachOutContactPhoneProps
 * @property {import('../CSTypes').CSLink} link
 */
/** @type {React.FC<ReachOutContactPhoneProps>} */
const ReachOutContactPhone = ({ link }) => (
	<div className="ReachOut__links">
		<span className="ReachOut__link">
			<i className="icon">phone</i>
			<a href={link.href} target={link.title}>
				{link.title}
			</a>
		</span>
	</div>
);

/** @type {React.FC<{ options: ReachOutCS }>} */
const ReachOutBody = ({ options }) => {
	if (!options?.body?.[0]) {
		return null;
	}

	const copy = GetCopyFromJson(options.body[0].copy);
	// React would like the children to be in an array.
	const toPrint = Array.isArray(copy) ? copy : [copy];

	return (
		<React.Fragment>
			<h1 className="ReachOut__title">{options.header}</h1>
			<ReachOutContactPhone link={options.body[0].phone_number_link} />
			<>{toPrint}</>
		</React.Fragment>
	);
};

export default ReachOut;
